/**
 * Utility functions for handling API errors, including quota and rate limit errors
 */
import type { ApiError, ErrorResult } from "../types/errors";
import { formatTimeUntil } from "./utils";

// Re-export types for backward compatibility
export type { ApiError, ErrorResult } from "../types/errors";

/**
 * Analyze API error and return user-friendly error information
 */
export function getErrorInfo(error: ApiError): ErrorResult {
    // Check quota errors FIRST before generic rate limits
    // Quota exceeded errors
    if (error.response?.data?.error?.code?.includes("QUOTA_EXCEEDED")) {
        const quota = error.response.data.error.quota;
        const quotaType = quota?.type || "unknown";

        let resourceName = "operations";
        if (quotaType.includes("story")) resourceName = "stories";
        if (quotaType.includes("drawing")) resourceName = "drawings";
        if (quotaType.includes("topic")) resourceName = "topic requests";

        const resetDate = quota?.resetDate;
        const resetTime = resetDate
            ? `${new Date(resetDate).toLocaleString()} (${formatTimeUntil(resetDate)})`
            : "tomorrow";

        return {
            type: "quota_exceeded",
            title: "Daily Limit Reached",
            message: `You've reached your daily limit for ${resourceName}. Your quota resets ${resetTime}.`,
            action: "try_later",
            resetDate,
        };
    }

    // Quota warning (approaching limit)
    if (error.response?.data?.error?.code?.includes("QUOTA_WARNING")) {
        const quota = error.response.data.error.quota;
        const remaining = quota ? quota.limit - quota.used : 0;

        return {
            type: "quota_warning",
            title: "Approaching Limit",
            message: `You have ${remaining} operations remaining today. Consider pacing your usage.`,
            action: "retry",
        };
    }

    // Rate limit errors (429 status or RATE_LIMITED code) - check AFTER quota errors
    if (error.response?.status === 429 || error.response?.data?.error?.code === "RATE_LIMITED") {
        const retryAfter = error.response?.data?.error?.retryAfter;
        return {
            type: "rate_limit",
            title: "Rate Limited",
            message: retryAfter
                ? `Too many requests. Please wait ${retryAfter} seconds and try again.`
                : "Too many requests. Please wait a moment and try again.",
            action: "wait",
        };
    }

    // Generic error fallback
    const errorMessage = error.response?.data?.error?.message || error.message || "Something went wrong";
    return {
        type: "generic",
        title: "Request Failed",
        message: errorMessage,
        action: "retry",
    };
}
